'use client';

import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import useAuthStore from '@/store/useAuthStore';
import { getQuestWithChapters } from '@/lib/questHelper';
import type { Quest } from '@/constant/schema';
import { buildTextbookTocData, type TextbookTocData } from '@/lib/textbook-toc';

const QuestPracticePopup = lazy(() =>
  import('@/components/Popup/quest-practice-popup').then((module) => ({
    default: module.QuestPracticePopup,
  }))
);

type ParentTextbookPreviewModalProps = {
  questId: string | null;
  initialQuest?: Quest | null;
  initialToc?: TextbookTocData | null;
  chapterId?: string | null;
  lessonId?: string | null;
  autoOpenAssign?: boolean;
  allowAssignActions?: boolean;
  visible: boolean;
  onAuthRequired?: () => void;
  onClose: () => void;
};

function withSearchPreviewParam(href: string) {
  const separator = href.includes('?') ? '&' : '?';
  return `${href}${separator}preview=1`;
}

export function ParentTextbookPreviewModal({
  questId,
  initialQuest = null,
  initialToc = null,
  chapterId = null,
  lessonId = null,
  autoOpenAssign = false,
  allowAssignActions = true,
  visible,
  onAuthRequired,
  onClose,
}: ParentTextbookPreviewModalProps) {
  const { user } = useAuthStore();
  const [quest, setQuest] = useState<Quest | null>(null);
  const previewTocChapters = useMemo(() => {
    const baseChapters =
      initialQuest?.id === questId && initialToc?.chapters?.length
        ? initialToc.chapters
        : buildTextbookTocData(quest?.book_name || quest?.title || 'Textbook', quest)?.chapters || [];

    return baseChapters.map((chapter) => ({
      ...chapter,
      lessons: chapter.lessons.map((lesson) => ({
        ...lesson,
        learnPath: withSearchPreviewParam(lesson.learnPath),
        practicePath: withSearchPreviewParam(lesson.practicePath),
      })),
    }));
  }, [initialQuest?.id, initialToc?.chapters, quest, questId]);

  useEffect(() => {
    let cancelled = false;

    async function loadQuest() {
      if (!visible || !questId) {
        setQuest(null);
        return;
      }

      if (initialQuest?.id === questId) {
        setQuest(initialQuest);
        return;
      }

      setQuest(null);
      try {
        const nextQuest = await getQuestWithChapters(questId);
        if (!cancelled) {
          setQuest(nextQuest);
        }
      } catch (error) {
        console.error('Failed to load quest preview:', error);
        if (!cancelled) {
          setQuest(null);
        }
      }
    }

    void loadQuest();

    return () => {
      cancelled = true;
    };
  }, [initialQuest, questId, visible]);

  return (
    <Suspense fallback={null}>
      <QuestPracticePopup
        quest={quest}
        visible={visible}
        userId={user?.id || ''}
        from={allowAssignActions ? 'parent-search' : 'search'}
        previewOnly={allowAssignActions}
        initialChapterId={chapterId}
        initialLessonId={lessonId}
        previewTocChapters={previewTocChapters}
        autoOpenAssign={autoOpenAssign}
        allowAssignActions={allowAssignActions}
        onAuthRequired={onAuthRequired}
        onClose={onClose}
      />
    </Suspense>
  );
}
